import i18next, { maybeTranslate } from "utilities/i18n";
import {
  EntitlementDataFragment,
  InstanceDataFragment,
  Ns_Ls_AuditEventType as AuditEventType,
  Ns_Ls_Entitlement,
  Ns_Ls_EntitlementState as EntitlementState,
  Ns_Ls_EntitlementType as EntitlementType,
  Ns_Ls_Instance,
  Ns_Ls_PolyRole as PolyRole,
  Ns_Ls_StandardEntitlementNames as StandardEntitlementNames,
  Ns_Ls_StandardEntitlementValueTypes as EntitlementValueType,
  Ns_Ls_StandardEntitlementValueTypes as StandardEntitlementValueTypes,
  Ns_Ls_StandardProductIds as StandardProductIds,
  Ns_Ls_UserRole as UserRole
} from "../generated/graphql";
import { capitalCase } from "capital-case";
import { dateToIntSecondsString, parseIntSecondsDate } from "./datetime";
import { DataValue, IDataValue } from "./dataUtils";
import { handleNullFields } from "./stringUtils";
import * as Roles from "../rbac";
import { needs, Requirement } from "../rbac";
import { OptionType } from "../elements";
import { arrayUnique } from "./arrayUtils";

export const LICENSE_MIN_BEGINS_DATE = new Date(0);
export const LICENSE_MIN_EXPIRY_DATE = new Date(0);
export const LICENSE_NO_BEGINS_DATE = new Date(-1);
export const LICENSE_NO_EXPIRY_DATE = new Date(-1);
export const UNKNOWN_TEXT = i18next.t("UNKNOWN");
export const NOT_SET_TEXT = i18next.t("NOT_SET");
export const LICENSE_DATE_IMMEDIATE_TEXT = i18next.t("LICENSE_DATE_IMMEDIATE");
export const LICENSE_DATE_PERMANENT_TEXT = i18next.t("LICENSE_DATE_PERMANENT");
export const LICENSE_UNLIMITED_COUNT = -1;
export const LICENSE_UNCOUNTED_TEXT = i18next.t("LICENSE_UNCOUNTED");
export const LICENSE_UNLIMITED_COUNT_TEXT = i18next.t(
  "LICENSE_UNLIMITED_COUNT"
);

export const POLY_AUDITOR_REQUIREMENT: Requirement = Roles.all([
  needs("license:PolyRead")
]);

export const POLY_ADMIN_REQUIREMENT: Requirement = Roles.all([
  needs("license:PolyRead"),
  needs("license:PolyWrite")
]);

export const POLY_SUPER_ADMIN_REQUIREMENT: Requirement = Roles.all([
  needs("license:PolyRead"),
  needs("license:PolyWrite"),
  needs("license:PolySuperAdmin")
]);

export const TENANT_AUDITOR_REQUIREMENT: Requirement = Roles.all([
  needs("license:ReadTenant")
]);

export const TENANT_ADMIN_REQUIREMENT: Requirement = Roles.all([
  needs("license:ReadTenant"),
  needs("license:WriteTenant")
]);

export enum EntitlementAssignmentType {
  Account = "ACCOUNT",
  Product = "PRODUCT"
}

/**
 * Export enum aliases without namespace prefix
 */
export {
  AuditEventType,
  EntitlementState,
  EntitlementType,
  EntitlementValueType,
  UserRole,
  PolyRole,
  StandardEntitlementNames,
  StandardEntitlementValueTypes,
  StandardProductIds
};

enum LangKeyPrefix {
  "AUDIT_EVENT_TYPE" = "AUDIT_EVENT_TYPE_",
  "PRODUCT_ID" = "PRODUCT_ID_",
  "ENTITLEMENT_TYPE" = "ENTITLEMENT_TYPE_",
  "ENTITLEMENT_VALUE_TYPE" = "ENTITLEMENT_VALUE_TYPE_",
  "ENTITLEMENT_STATE" = "ENTITLEMENT_STATE_",
  "ENTITLEMENT_ASSIGNMENT_TYPE" = "ENTITLEMENT_ASSIGNMENT_TYPE_",
  "ENTITLEMENT_NAME" = "ENTITLEMENT_NAME_"
}

// Audit Event Type Translations

export function getAuditEventType(
  type: AuditEventType | string,
  defaultValue?: string
): string | undefined {
  if (!type) {
    return type;
  }

  const langKey = `${LangKeyPrefix.AUDIT_EVENT_TYPE}${type}` as const;
  return maybeTranslate(langKey, defaultValue);
}

export const AuditEventTypeMap = new Map<string, AuditEventType>(
  Object.entries(AuditEventType).map(([key, value]) => [
    typeof value === "string" ? value : key,
    value
  ])
);

// Entitlement Type Translations

export function getEntitlementType(
  type: EntitlementType | string,
  defaultValue?: string
): string | undefined {
  if (!type) {
    return type;
  }

  const langKey = `${LangKeyPrefix.ENTITLEMENT_TYPE}${type}` as const;
  return maybeTranslate(langKey, defaultValue);
}

export const EntitlementTypeMap = new Map<string, EntitlementType>(
  Object.entries(EntitlementType).map(([key, value]) => [
    typeof value === "string" ? value : key,
    value as EntitlementType
  ])
);

export type EntitlementTypeOptionType = OptionType & {
  type: EntitlementType;
};

export const EntitlementTypeToOptionTypeMap = new Map<
  EntitlementType,
  EntitlementTypeOptionType
>(
  Object.entries(EntitlementType).map(([key, value]) => {
    const type = value as EntitlementType;
    const typeId = typeof value === "string" ? value : key;
    const optionType: EntitlementTypeOptionType = {
      type: type,
      value: value,
      text: handleNullFields(getEntitlementType(typeId))
    };

    return [type, optionType];
  })
);

export const EntitlementTypeOptionTypes: Array<EntitlementTypeOptionType> = Array.from(
  EntitlementTypeToOptionTypeMap.values()
).sort((a, b) => a.text.localeCompare(b.text));

// Entitlement Value Type Translations

export function getEntitlementValueType(
  valueType: EntitlementValueType | string,
  defaultValue?: string
): string | undefined {
  if (!valueType) {
    return valueType;
  }

  const langKey = `${LangKeyPrefix.ENTITLEMENT_VALUE_TYPE}${valueType}` as const;
  return maybeTranslate(langKey, defaultValue);
}

export type EntitlementValueTypeOptionType = OptionType & {
  type: EntitlementValueType;
};

export const EntitlementValueTypeMap = new Map<string, EntitlementValueType>(
  Object.entries(EntitlementValueType).map(([key, value]) => [
    typeof value === "string" ? value : key,
    value as EntitlementValueType
  ])
);

export const EntitlementValueTypeToOptionTypeMap = new Map<
  EntitlementValueType,
  EntitlementValueTypeOptionType
>(
  Object.entries(EntitlementValueType).map(([key, value]) => {
    const type = value as EntitlementValueType;
    const valueType = typeof value === "string" ? value : key;
    const optionType: EntitlementValueTypeOptionType = {
      type: type,
      value: value,
      text: handleNullFields(getEntitlementValueType(valueType))
    };
    return [type, optionType];
  })
);

export const EntitlementValueTypeOptionTypes: Array<EntitlementValueTypeOptionType> = Array.from(
  EntitlementValueTypeToOptionTypeMap.values()
)
  .filter(optionType => optionType.type !== EntitlementValueType.Unknown)
  .sort((a, b) => a.text.localeCompare(b.text));

// Entitlement State Translations

export function getEntitlementState(
  state: EntitlementState | string,
  defaultValue?: string
): string | undefined {
  if (!state) {
    return state;
  }

  const langKey = `${LangKeyPrefix.ENTITLEMENT_STATE}${state}` as const;
  return maybeTranslate(langKey, defaultValue);
}

export const EntitlementStateMap = new Map<string, EntitlementState>(
  Object.entries(EntitlementState).map(([key, value]) => [
    typeof value === "string" ? value : key,
    value as EntitlementState
  ])
);

export type EntitlementStateOptionType = OptionType & {
  type: EntitlementState;
};

export const EntitlementStateToOptionTypeMap = new Map<
  EntitlementState,
  EntitlementStateOptionType
>(
  Object.entries(EntitlementState).map(([key, value]) => {
    const type = value as EntitlementState;
    const state = typeof value === "string" ? value : key;
    const optionType: EntitlementStateOptionType = {
      type: type,
      value: value,
      text: handleNullFields(getEntitlementState(state))
    };
    return [type, optionType];
  })
);

export const EntitlementStateOptionTypes: Array<EntitlementStateOptionType> = Array.from(
  EntitlementStateToOptionTypeMap.values()
).sort((a, b) => a.text.localeCompare(b.text));

// Entitlement Assignment Type Translations

export function getEntitlementAssignmentType(
  assignmentType: EntitlementAssignmentType | string,
  defaultValue?: string
): string | undefined {
  if (!assignmentType) {
    return assignmentType;
  }

  const langKey = `${LangKeyPrefix.ENTITLEMENT_ASSIGNMENT_TYPE}${assignmentType}` as const;
  return maybeTranslate(langKey, defaultValue);
}

export type EntitlementAssignmentTypeOptionType = OptionType & {
  type: EntitlementAssignmentType;
};

export const EntitlementAssignmentTypeMap = new Map<
  string,
  EntitlementAssignmentType
>(
  Object.entries(EntitlementAssignmentType).map(([key, value]) => [
    typeof value === "string" ? value : key,
    value as EntitlementAssignmentType
  ])
);

export const EntitlementAssignmentTypeToOptionTypeMap = new Map<
  EntitlementAssignmentType,
  EntitlementAssignmentTypeOptionType
>(
  Object.entries(EntitlementAssignmentType).map(([key, value]) => {
    const type = value as EntitlementAssignmentType;
    const valueType = typeof value === "string" ? value : key;
    const optionType: EntitlementAssignmentTypeOptionType = {
      type: type,
      value: value,
      text: handleNullFields(getEntitlementAssignmentType(valueType))
    };
    return [type, optionType];
  })
);

export const EntitlementAssignmentTypeOptionTypes: Array<EntitlementAssignmentTypeOptionType> = Array.from(
  EntitlementAssignmentTypeToOptionTypeMap.values()
).sort((a, b) => a.text.localeCompare(b.text));

// Entitlement Name Translations

export function getEntitlementName(
  nameId: StandardEntitlementNames | string,
  defaultValue?: string
): string | undefined {
  if (!nameId) {
    return nameId;
  }

  const langKey = `${LangKeyPrefix.ENTITLEMENT_NAME}${nameId}` as const;
  return maybeTranslate(langKey, defaultValue);
}

export const StandardEntitlementNameMap = new Map<
  string,
  StandardEntitlementNames
>(
  Object.entries(StandardEntitlementNames).map(([key, value]) => [
    typeof value === "string" ? value : key,
    value as StandardEntitlementNames
  ])
);

export const AvailableEntitlementNames = new Set<StandardEntitlementNames>([
  StandardEntitlementNames.AudioDevices,
  StandardEntitlementNames.ClaritiManagerBaseLicense,
  StandardEntitlementNames.ElasticConcurrentAudioCalls,
  StandardEntitlementNames.ElasticConcurrentCalls,
  StandardEntitlementNames.ElasticConcurrentP2PCalls,
  StandardEntitlementNames.ElasticConcurrentVmrs,
  StandardEntitlementNames.LongTermLicensing,
  StandardEntitlementNames.MediaEncryption,
  StandardEntitlementNames.PerpetualConcurrentAudioCalls,
  StandardEntitlementNames.PerpetualConcurrentCalls,
  StandardEntitlementNames.PerpetualConcurrentP2PCalls,
  StandardEntitlementNames.PerpetualConcurrentVmrs,
  StandardEntitlementNames.SubscriptionConcurrentAudioCalls,
  StandardEntitlementNames.SubscriptionConcurrentCalls,
  StandardEntitlementNames.SubscriptionConcurrentP2PCalls,
  StandardEntitlementNames.SubscriptionConcurrentVmrs,
  StandardEntitlementNames.VideoDevices
]);

export type StandardEntitlementNameOptionType = OptionType & {
  type: StandardEntitlementNames;
};

export const StandardEntitlementNameToOptionTypeMap = new Map<
  StandardEntitlementNames,
  StandardEntitlementNameOptionType
>(
  Object.entries(StandardEntitlementNames).map(([key, value]) => {
    const type = value as StandardEntitlementNames;
    const nameId = typeof value === "string" ? value : key;
    const optionType: StandardEntitlementNameOptionType = {
      type: type,
      value: value,
      text: handleNullFields(getEntitlementName(nameId))
    };
    return [type, optionType];
  })
);

export const AvailableEntitlementNameOptionTypes: Array<StandardEntitlementNameOptionType> = Array.from(
  StandardEntitlementNameToOptionTypeMap.values()
)
  .filter(optionType => AvailableEntitlementNames.has(optionType.type))
  .sort((a, b) => a.text.localeCompare(b.text));

// Product Name Translations

export function getProductName(
  productId: StandardProductIds | string,
  defaultValue?: string
): string | undefined {
  if (!productId) {
    return productId;
  }

  const langKey = `${LangKeyPrefix.PRODUCT_ID}${productId}` as const;
  return maybeTranslate(langKey, defaultValue);
}

export const AvailableProductIds = new Set<StandardProductIds>([
  StandardProductIds.ClaritiCe,
  StandardProductIds.ClaritiManager
]);

export type StandardProductOptionType = OptionType & {
  type: StandardProductIds;
};

export const StandardProductIdToOptionTypeMap = new Map<
  StandardProductIds,
  StandardProductOptionType
>(
  Object.entries(StandardProductIds).map(([key, value]) => {
    const type = value as StandardProductIds;
    const productId = typeof value === "string" ? value : key;
    const optionType: StandardProductOptionType = {
      type: type,
      value: value,
      text: handleNullFields(getProductName(productId))
    };
    return [type, optionType];
  })
);

export const AvailableProductOptionTypes: Array<StandardProductOptionType> = Array.from(
  StandardProductIdToOptionTypeMap.values()
)
  .filter(optionType => AvailableProductIds.has(optionType.type))
  .sort((a, b) => a.text.localeCompare(b.text));

export function getInstanceIdentifiableName(
  instance: Pick<Ns_Ls_Instance, "niceName" | "serialNumber">
) {
  const niceName = instance.niceName.trim();
  return niceName.length > 0
    ? niceName
    : i18next.t("PURCHASED_PRODUCT_IDENTIFIABLE_NAME", {
        name: NOT_SET_TEXT,
        serialNumber: instance.serialNumber.trim() || NOT_SET_TEXT
      });
}

export function toAuditEventType(state: string): AuditEventType {
  return AuditEventTypeMap.get(state) ?? AuditEventType.Unknown;
}

export function toStandardEntitlementName(
  name: string
): StandardEntitlementNames {
  return (
    StandardEntitlementNameMap.get(name) ?? StandardEntitlementNames.Unknown
  );
}

export function toEntitlementAssignmentType(
  assignedToTenant: boolean
): EntitlementAssignmentType {
  return assignedToTenant
    ? EntitlementAssignmentType.Account
    : EntitlementAssignmentType.Product;
}

export function toEntitlementAssignmentType2(
  type: string
): EntitlementAssignmentType {
  return (
    EntitlementAssignmentTypeMap.get(type) ?? EntitlementAssignmentType.Product
  );
}

export function toEntitlementType(state: string): EntitlementType {
  return EntitlementTypeMap.get(state) ?? EntitlementType.Normal;
}

export function toEntitlementValueType(type: string): EntitlementValueType {
  return EntitlementValueTypeMap.get(type) ?? EntitlementValueType.Unknown;
}

export function toEntitlementState(state: string): EntitlementState {
  return EntitlementStateMap.get(state) ?? EntitlementState.Normal;
}

export function toCapitalCase(value: string): string {
  return value.trim().length < 1 ? NOT_SET_TEXT : capitalCase(value);
}

export function toBooleanString(value: boolean): string {
  return value ? i18next.t("YES") : i18next.t("NO");
}

export function toCountedNumberString(value: number): string {
  return value < 0 ? NOT_SET_TEXT : "" + value;
}

/**
 * Helper method for converting dates to integer based number of seconds as a string
 * type.  Any value of 0 or less will result in a not set value which is "-1" by default.
 *
 * @param date
 * @param notSetValue
 */
export function toLicenseDateIntSecondsString(
  date: Date | number,
  notSetValue: string = "-1"
): string {
  if (typeof date === "number") {
    date = new Date(date);
  }
  return date.getTime() <= 0 ? notSetValue : dateToIntSecondsString(date);
}

/**
 * Helper method for displaying dates whereby a date timestamp of 0 or less means not set.
 *
 * @param date
 * @param notSetText
 */
export function toLicenseDateLocaleDateString(
  date: Date | number | undefined,
  notSetText: string = NOT_SET_TEXT
): string {
  if (typeof date === "number") {
    date = new Date(date);
  }
  return date !== undefined
    ? date.getTime() <= 0
      ? notSetText
      : date.toLocaleString()
    : UNKNOWN_TEXT;
}

/**
 * Helper method for displaying licensing start dates whereby a date timestamp of 0 or less means the license
 * has no start date (immediate).
 *
 * @param date
 */
export function toLicenseBeginsLocaleDateString(
  date: Date | number | undefined
): string {
  return toLicenseDateLocaleDateString(date, LICENSE_DATE_IMMEDIATE_TEXT);
}

/**
 * Helper method for displaying licensing start dates whereby a date timestamp of 0 or less means the license
 * has no expiry (permanent).
 *
 * @param date
 */
export function toLicenseExpiryLocaleDateString(
  date: Date | number | undefined
): string {
  return toLicenseDateLocaleDateString(date, LICENSE_DATE_PERMANENT_TEXT);
}

export function toLicenseUnlimitedCountString(
  count: number | undefined
): string {
  return count !== undefined
    ? count <= LICENSE_UNLIMITED_COUNT
      ? LICENSE_UNLIMITED_COUNT_TEXT
      : "" + count
    : UNKNOWN_TEXT;
}

export function isEntitlementAwaitingRelease(state: EntitlementState): boolean {
  return state === EntitlementState.AwaitingRelease;
}

export function isEntitlementAssigned(state: EntitlementState): boolean {
  return state === EntitlementState.Assigned;
}

export function isEntitlementNormal(state: EntitlementState): boolean {
  return state === EntitlementState.Normal;
}

export function isEntitlementUsed(state: EntitlementState): boolean {
  return state === EntitlementState.Used;
}

export function isEntitlementOrphaned(state: EntitlementState): boolean {
  return state === EntitlementState.Orphaned;
}

export function areAllEntitlementsAssigned(
  entitlements: Array<EntitlementModel>
): boolean {
  return entitlements.length > 0
    ? entitlements.findIndex(entitlement => !entitlement.assigned) < 0
    : false;
}

export function areAllEntitlementsAssignedOrUsed(
  entitlements: Array<EntitlementModel>
): boolean {
  return entitlements.length > 0
    ? entitlements.findIndex(
        entitlement => !(entitlement.assigned || entitlement.used)
      ) < 0
    : false;
}

export function areAllEntitlementsNormal(
  entitlements: Array<EntitlementModel>
): boolean {
  return entitlements.length > 0
    ? entitlements.findIndex(entitlement => !entitlement.normal) < 0
    : false;
}

export function areAllEntitlementsAssignable(
  entitlements: Array<EntitlementModel>
): boolean {
  return entitlements.length > 0
    ? entitlements.findIndex(entitlement => !entitlement.assignable) < 0
    : false;
}

export function areAllEntitlementsReleasable(
  entitlements: Array<EntitlementModel>
): boolean {
  return entitlements.length > 0
    ? entitlements.findIndex(
        entitlement =>
          entitlement.assignmentType.data ===
            EntitlementAssignmentType.Account || entitlement.normal
      ) < 0
    : false;
}

export function doAllInstancesHaveLicense(
  instances: Array<InstanceModel>
): boolean {
  return instances.length > 0
    ? instances.findIndex(instance => !instance.hasLicense.data) < 0
    : false;
}

/**
 * Tests 2 version strings (x.y.z-abcd or some similar format) and determines if the values are different
 * up to some specified number of places. For example: 1.2.3 and 1.2.4 are different in the 3rd place, but
 * are the same for the 2nd place. Version strings are parsed using either dash (-) or period (.) as place
 * delimiters.
 *
 * @param versionString1               String A with version value to compare.
 * @param versionString2               String B with version value to compare.
 * @param significantComparisionPlaces number of places in parsed version string to compare (most to least
 *                                     significant).
 * @return a negative integer, zero, or a positive integer as versionString1 is less than, equal to, or
 *         greater than versionString2 to the specified number of comparison places.
 */
export function versionsCompare(
  versionString1: string,
  versionString2: string,
  significantComparisionPlaces: number = 2
) {
  if (significantComparisionPlaces < 1) {
    return 0;
  }

  if (versionString1 === versionString2) {
    return 0;
  }

  if (!versionString1) {
    return -1;
  }

  if (!versionString2) {
    return 1;
  }

  const v1Parts = versionString1.split("[_.-]+");
  const v2Parts = versionString2.split("[_.-]+");

  for (let i = 1; i <= significantComparisionPlaces; i++) {
    let t1 = i > v1Parts.length ? "0" : v1Parts[i - 1];
    let t2 = i > v2Parts.length ? "0" : v2Parts[i - 1];
    if (undefined === t1) {
      t1 = "0";
    }

    if (undefined === t2) {
      t2 = "0";
    }

    try {
      const t1int = parseInt(t1);
      const t2int = parseInt(t2);
      if (t1int < t2int) {
        return -1;
      }

      if (t1int > t2int) {
        return 1;
      }
    } catch (e) {
      const c = t1.localeCompare(t2);
      if (c < 0) {
        return -1;
      }
      if (c > 0) {
        return 1;
      }
    }
  }
  return 0;
}

function newDataValueString(value: string): IDataValue<string> {
  return new DataValue<string>(
    value,
    thisValue => handleNullFields(thisValue.data),
    thisValue => thisValue.text
  );
}

function newDataValueBoolean(value: boolean): IDataValue<boolean> {
  return new DataValue<boolean>(
    value,
    thisValue => toBooleanString(thisValue.data),
    thisValue => thisValue.text
  );
}

function newDataValueAny(value: any): IDataValue<any> {
  return new DataValue<any>(
    value,
    thisValue => thisValue.data,
    thisValue => thisValue.text
  );
}

function newDateValueLicenseUnlimitedCountNumber(
  value: number
): IDataValue<number> {
  return new DataValue<number>(value, thisValue =>
    toLicenseUnlimitedCountString(thisValue.data)
  );
}

function newDataValueLicenseDate(value: Date): IDataValue<Date> {
  return new DataValue<Date>(value, thisValue =>
    toLicenseDateLocaleDateString(thisValue.data)
  );
}

function newDateValueLicenseExpiryDate(value: Date): IDataValue<Date> {
  return new DataValue<Date>(value, thisValue =>
    toLicenseExpiryLocaleDateString(thisValue.data)
  );
}

function newDataValueEntitlementState(
  value: EntitlementState
): IDataValue<any> {
  return new DataValue<EntitlementState>(
    value,
    thisValue => handleNullFields(getEntitlementState(value)),
    thisValue => thisValue.text
  );
}

function newDataValueEntitlementNameString(
  value: StandardEntitlementNames
): IDataValue<string> {
  return new DataValue<string>(
    value,
    thisValue => handleNullFields(getEntitlementName(value)),
    thisValue => thisValue.text
  );
}

function newDataValueProductNameString(
  value: StandardProductIds
): IDataValue<string> {
  return new DataValue<string>(
    value,
    thisValue => handleNullFields(getProductName(value)),
    thisValue => thisValue.text
  );
}

export type EntitlementData = EntitlementDataFragment & Ns_Ls_Entitlement & {};

export type EntitlementDataEdge = {
  cursor: any;
  node: EntitlementData;
};

export class EntitlementModel {
  readonly recordId: string;
  readonly tenantId: string;
  readonly assignedToRecordId: string;
  readonly pendingAssignedToRecordId: string;
  readonly cursor: any;
  readonly name: IDataValue<string>;
  readonly productId: IDataValue<string>;
  readonly productVersion: IDataValue<string>;
  readonly type: IDataValue<EntitlementType>;
  readonly state: IDataValue<EntitlementState>;
  readonly value: IDataValue<number>;
  readonly stringValue: IDataValue<string>;
  readonly valueType: IDataValue<EntitlementValueType>;
  readonly assignmentType: IDataValue<EntitlementAssignmentType>;
  readonly creationTime: IDataValue<Date>;
  readonly lastModifiedTime: IDataValue<Date>;
  readonly expires: IDataValue<Date>;

  constructor(edgeData: EntitlementDataEdge) {
    const nodeData: EntitlementData = edgeData.node;

    this.recordId = nodeData.recordId;
    this.tenantId = nodeData.tenantId;
    this.assignedToRecordId = nodeData.assignedToRecordId;
    this.pendingAssignedToRecordId = nodeData.pendingAssignedToRecordId;
    this.cursor = edgeData.cursor;
    this.name = newDataValueEntitlementNameString(
      nodeData.name as StandardEntitlementNames
    );
    this.productId = newDataValueProductNameString(
      nodeData.productId as StandardProductIds
    );
    this.productVersion = newDataValueString(nodeData.productVersion);
    this.type = newDataValueAny(toEntitlementType(nodeData.type));
    this.state = newDataValueEntitlementState(
      toEntitlementState(nodeData.state)
    );
    this.value = newDateValueLicenseUnlimitedCountNumber(nodeData.value);
    this.stringValue = newDataValueString(nodeData.stringValue);
    this.valueType = newDataValueAny(
      toEntitlementValueType(nodeData.valueType)
    );
    this.assignmentType = newDataValueAny(
      toEntitlementAssignmentType(nodeData.assignedToTenant)
    );
    this.creationTime = newDataValueLicenseDate(
      parseIntSecondsDate(nodeData.creationTime)
    );
    this.lastModifiedTime = newDataValueLicenseDate(
      parseIntSecondsDate(nodeData.lastModifiedTime)
    );
    this.expires = newDateValueLicenseExpiryDate(
      parseIntSecondsDate(nodeData.expires)
    );
  }

  get counted(): boolean {
    return this.valueType.data === EntitlementValueType.Counted;
  }

  get awaitingRelease(): boolean {
    return isEntitlementAwaitingRelease(this.state.data);
  }

  get assigned(): boolean {
    return isEntitlementAssigned(this.state.data);
  }

  get normal(): boolean {
    return isEntitlementNormal(this.state.data);
  }

  get used(): boolean {
    return isEntitlementUsed(this.state.data);
  }

  get orphaned(): boolean {
    return isEntitlementOrphaned(this.state.data);
  }

  get assignable(): boolean {
    return (
      this.normal &&
      this.assignmentType.data !== EntitlementAssignmentType.Account
    );
  }

  isPendingAssignmentToInstance(instanceRecordId: string): boolean {
    return (
      !!this.pendingAssignedToRecordId.length &&
      this.pendingAssignedToRecordId === instanceRecordId &&
      (this.state.data === EntitlementState.AwaitingRelease ||
        this.state.data === EntitlementState.PendingReleaseAck)
    );
  }
}

export type InstanceData = InstanceDataFragment & Ns_Ls_Instance & {};

export type InstanceDataEdge = {
  cursor: any;
  node: InstanceData;
};

export class InstanceModel {
  readonly recordId: string;
  readonly tenantId: string;
  readonly instanceId: string;
  readonly cursor: any;
  readonly name: IDataValue<string>;
  readonly productId: IDataValue<string>;
  readonly productVersion: IDataValue<string>;
  readonly serialNumber: IDataValue<string>;
  readonly hostName: IDataValue<string>;
  readonly useAlternateLicencing: IDataValue<boolean>;
  readonly entitlements: Array<string>;
  readonly pendingEntitlements: Array<string>;
  readonly allEntitlements: Array<string>;
  readonly creationTime: IDataValue<Date>;
  readonly lastLicenseExpires: IDataValue<Date>;
  readonly lastLicenseHadExpiredEntitlements: IDataValue<boolean>;
  readonly lastLicensedTime: IDataValue<Date>;
  readonly lastModifiedTime: IDataValue<Date>;
  readonly hasLicense: IDataValue<boolean>;
  readonly hasInitialEncodedRequest: IDataValue<boolean>;
  readonly hasServerChainToken: IDataValue<boolean>;

  constructor(edgeData: InstanceDataEdge) {
    const nodeData: InstanceData = edgeData.node;

    this.recordId = nodeData.recordId;
    this.tenantId = nodeData.tenantId;
    this.instanceId = nodeData.instanceId;
    this.cursor = edgeData.cursor;
    this.name = newDataValueString(nodeData.niceName);
    this.productId = newDataValueProductNameString(
      nodeData.productId as StandardProductIds
    );
    this.productVersion = newDataValueString(nodeData.productVersion);
    this.serialNumber = newDataValueString(nodeData.serialNumber);
    this.hostName = newDataValueString(nodeData.hostname);
    this.useAlternateLicencing = newDataValueBoolean(
      nodeData.useAlternateLicencing
    );
    this.entitlements = [...nodeData.entitlements];
    this.pendingEntitlements = [...nodeData.pendingEntitlements];
    this.allEntitlements = arrayUnique([
      ...nodeData.entitlements,
      ...nodeData.pendingEntitlements
    ]);
    this.creationTime = newDataValueLicenseDate(
      parseIntSecondsDate(nodeData.creationTime)
    );
    this.lastLicenseExpires = newDataValueLicenseDate(
      parseIntSecondsDate(nodeData.lastLicenseExpires)
    );
    this.lastLicenseHadExpiredEntitlements = newDataValueBoolean(
      nodeData.lastLicenseHadExpiredEntitlements
    );
    this.lastLicensedTime = newDataValueLicenseDate(
      parseIntSecondsDate(nodeData.lastLicensedTime)
    );
    this.lastModifiedTime = newDataValueLicenseDate(
      parseIntSecondsDate(nodeData.lastModifiedTime)
    );
    this.hasLicense = newDataValueBoolean(nodeData.hasEncodedLicense);
    this.hasInitialEncodedRequest = newDataValueBoolean(
      nodeData.hasInitialEncodedRequest
    );
    this.hasServerChainToken = newDataValueBoolean(
      nodeData.hasServerChainToken
    );
  }

  get identifiableName(): string {
    return getInstanceIdentifiableName({
      niceName: this.name.data,
      serialNumber: this.serialNumber.text
    });
  }
}

export function newInstanceModel(data: InstanceData): InstanceModel {
  return new InstanceModel({
    node: data,
    cursor: undefined
  });
}

export function newEntitlementModel(data: EntitlementData): EntitlementModel {
  return new EntitlementModel({
    node: data,
    cursor: undefined
  });
}
